/**
 * @fileoverview Main router configuration for the application.
 * Defines all routes and their corresponding components, including authentication flows,
 * password management, and application-specific routes.
 *
 * @example
 * // Usage in index.tsx or App.tsx
 * import { Router } from './router'
 *
 * function App() {
 *   return <Router />
 * }
 */


import {
    createBrowserRouter,
    createRoutesFromElements,
    Navigate,
    Route,
    RouterProvider,
} from 'react-router-dom'
import CreateAccount from './components/CreateAccount'
import ChangePassword from './components/login/ChangePassword'
import InviteExpired from './components/login/InviteExpired'
import LinkExpired from './components/login/LinkExpired'
import Login from './components/login/Login'
import RecoverPassword from './components/login/RecoverPassword'
import ResetPassword from './components/login/ResetPassword'
import { PublicRoot } from './components/PublicRoot'
import { StudyLoader } from './components/StudyLoader'
import { renderParticipantRoutes } from './routes/participants'
import { renderSitesRoutes } from './routes/sites'
import { renderStaffRoutes } from './routes/staff'
import { renderStudiesRoutes } from './routes/studies'

/**
 * @function Router
 * @description The main router configuration for the application.
 * @returns {React.ReactNode} - The router provider component.
 */
export function Router() {
    const router = createBrowserRouter(
        createRoutesFromElements(
            <Route path="/" element={<PublicRoot />}>
                <Route path="/login" element={<Login />} />
                <Route path="/invite" element={<CreateAccount />} />
                <Route path="/inviteExpired" element={<InviteExpired />} />
                <Route path="/password/request" element={<ResetPassword />} />
                <Route path="/password/change" element={<ChangePassword />} />
                <Route path="/password/recovery" element={<RecoverPassword />} />
                <Route path="/password/expired" element={<LinkExpired />} />
                <Route path="/app">
                    {renderStudiesRoutes()}
                    {renderParticipantRoutes()}
                    {renderSitesRoutes()}
                    {renderStaffRoutes()}
                    <Route index element={<Navigate to="participants" replace />} />
                    <Route index element={<StudyLoader />} />
                    <Route index element={<Navigate to="/app/study" replace />} />
                </Route>
                <Route index element={<Navigate to="/login" replace />} />
            </Route>
        )
    )

    return <RouterProvider router={router} />
}
